import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import "./style.css";
import "../../../styles/styleGlobal.css";
import { jwtDecode } from "jwt-decode";
import {
  API_ENDPOINTS,
  API_BASE_URL,
  limparSessaoEstudante,
} from "../../../js/api.js";

const Inicial = () => {
  const navigate = useNavigate();
  // const [cpf, setCpf] = useState('');
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  // const [codigoempresa, setCodigoempresa] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Flag compartilhada entre as tentativas de login em paralelo:
  // quando uma delas navega (sucesso), a outra DEVE abortar antes de
  // sobrescrever token/localStorage/navigate. Sem isso, o /tokens
  // rodando em paralelo pode sobrescrever o token do /tce/login-estudante
  // e redirecionar pra /tce sem o ?id=86 — quebrando o fluxo do estudante
  // (cairia em "Nenhuma autorização vinculada").
  const navigatedRef = useRef(false);

  function validarCPF(cpf) {
    cpf = cpf.replace(/\D/g, "");
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

    let soma = 0,
      resto;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;

    return true;
  }

  const formatUsuario = (value) => {
    const digits = value.replace(/\D/g, "");

    if (digits.length <= 6) {
      return digits;
    }

    let cpf = digits.slice(0, 11);

    cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
    cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
    cpf = cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");

    return cpf;
  };

  const handleUsuarioChange = (e) => {
    setUsuario(formatUsuario(e.target.value));
  };

  // Tenta autenticar o estudante via /tce/login-estudante.
  //
  // Comportamento esperado: este endpoint foi feito pra ser usado apenas
  // por estudantes (nível 1). Pra QUALQUER outro tipo de usuário
  // (SECEX, CIEE, empresa, CPF sem TCE vinculado), o backend rejeita
  // com 401/403/404 — comportamento correto. Nestes casos, o frontend
  // deve cair pro fluxo normal de login de empresa (/tokens) em vez de
  // exibir erro ao usuário.
  //
  // Retorna um objeto com `kind`:
  //   'success'  → login-estudante teve sucesso (token salvo, navegação
  //                já disparada). Caller não deve prosseguir.
  //   'rejected' → login-estudante rejeitou explicitamente (401/403/404).
  //                O usuário não é estudante; caller deve tentar /tokens.
  //   'error'    → erro de autenticação "real" (ex.: 400/500 com mensagem
  //                do backend, ou sem token no 200). Caller NÃO deve
  //                prosseguir; o erro já foi exibido.
  //   'network'  → erro de rede. Caller decide o que fazer.
  const tentarLoginEstudante = async (usuarioLimpo, requestBody) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tce/login-estudante`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json().catch(() => null);

      // Quando o backend rejeita explicitamente (401/403/404), o usuário
      // provavelmente não é estudante. Cai pro fluxo de empresa sem
      // mostrar erro, deixando o /tokens decidir o que fazer.
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        return { kind: "rejected" };
      }

      if (!response.ok) {
        const msg =
          data?.errors?.[0] ||
          data?.message ||
          "Erro ao fazer login do estudante.";
        setErrors({ user: msg });
        return { kind: "error" };
      }

      if (data?.token) {
        // SEMPRE prioridade: se o /tce/login-estudante retornou 200,
        // é o caminho certo pro estudante. Mesmo que /tokens tenha
        // chegado antes e já setado navigatedRef (cenário: o CPF do
        // estudante TAMBÉM existe como usuário de empresa), o login
        // do estudante sobrescreve com o tce_id correto.
        navigatedRef.current = true;
        localStorage.setItem("token", data.token);
        const decoded = jwtDecode(data.token);

        // usuário logado mantém forma consistente com o login de empresa
        // (inclui acesso_autorizacao, tipo_programa quando aplicável, etc.)
        const userData = {
          ...decoded,
          acesso_autorizacao: Number(decoded.acesso_autorizacao ?? 1),
          tipo_programa: 0,
        };
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("estudante_id", decoded.id);

        // Backend pode retornar explicitamente a autorização vinculada.
        // Caso não envie, cairá no fluxo de "sem ?id" — o TCE carrega o
        // próprio estudante_id e abre a autorização correspondente.
        const tceId =
          data?.tce_id ?? data?.autorizacao_id ?? data?.id_tce ?? null;
        if (tceId) {
          // Persiste também no localStorage: se o usuário recarregar a
          // página (ou se algo limpar a URL), o TCE ainda consegue
          // encontrar a autorização via fallback. Defesa contra o
          // problema "Nenhuma autorização vinculada" aparecer quando
          // há de fato uma autorização vinculada.
          localStorage.setItem("estudante_tce_id", String(tceId));
          navigate(`/tce?id=${tceId}`);
        } else {
          navigate("/tce");
        }
        return { kind: "success" };
      }

      const msg = data?.errors?.[0] || "Erro ao fazer login do estudante.";
      setErrors({ user: msg });
      return { kind: "error" };
    } catch (err) {
      console.error("Erro no login do Estudante:", err);
      // Erro de rede — deixa o caller decidir se cai pro login de empresa.
      return { kind: "network" };
    }
  };

  // Login de empresa via /tokens. Retorna { kind } igual ao de estudante:
  //   'success' → navegou (já salvou token, localStorage, etc.)
  //   'error'   → backend respondeu com erro já tratado (401/403/400/5xx/sem token)
  //   'network' → fetch caiu (queda de rede)
  const tentarLoginEmpresa = async (usuarioLimpo, senha) => {
    const requestBody = { usuario: usuarioLimpo, senha };
    let response = null;
    let data = null;

    try {
      response = await fetch(API_ENDPOINTS.inicial_tokens, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
    } catch (err) {
      console.error("Erro de rede ao chamar /tokens:", err);
      setErrors({ user: "Erro ao conectar com o servidor." });
      return { kind: "error" };
    }

    try {
      data = await response.json().catch(() => null);
    } catch (e) {
      data = null;
    }

    console.log("Login response", response && response.status, data);

    if (!response) {
      setErrors({ user: "Sem resposta do servidor." });
      return { kind: "error" };
    }

    if (response.status === 401) {
      console.error("Login 401 Unauthorized");
      const userMsg =
        (data && (data.message || (data.errors && data.errors[0]))) ||
        "Credenciais inválidas ou acesso não autorizado.";
      setErrors({ user: userMsg });
      return { kind: "error" };
    }

    // Tarefa B — Login 403 (acesso negado por regra de negócio).
    // NUNCA redirecionar para /login: já estamos nele.
    // Apenas exibir a mensagem retornada pelo backend.
    if (response.status === 403) {
      console.warn("Login 403 Forbidden (acesso negado por política)");
      const userMsg =
        (data && (data.message || (data.errors && data.errors[0]))) ||
        "Usuário sem permissão de acesso. Contate o administrador.";
      setErrors({ user: userMsg });
      return { kind: "error" };
    }

    if (response.status === 400) {
      let msg = "Requisição inválida (400). Verifique os campos.";
      if (data) {
        if (data.message) msg = data.message;
        else if (Array.isArray(data.errors)) {
          msg = data.errors
            .map((e) => (e && (e.message || e.msg)) || e)
            .join("; ");
        } else {
          try {
            msg = JSON.stringify(data);
          } catch (e) {
            /* ignore */
          }
        }
      }
      setErrors({ user: msg });
      return { kind: "error" };
    }

    if (response.status >= 500) {
      const msg =
        (data &&
          (data.message ||
            (data.errors && data.errors[0]) ||
            JSON.stringify(data))) ||
        "Erro interno do servidor (500). Verifique os logs do backend.";
      setErrors({ user: `Erro do servidor: ${msg}` });
      console.error("Servidor 5xx body:", data);
      return { kind: "error" };
    }

    if (response.ok && data && data.token) {
      // Se o /tce/login-estudante já navegou antes da gente, aborta
      // silenciosamente. Sem essa guarda, o token do /tokens SOBRESCREVE
      // o token do estudante e a rota vai para /tce SEM ?id=86, fazendo
      // o estudante cair na tela "Nenhuma autorização vinculada".
      if (navigatedRef.current) {
        return { kind: "rejected" };
      }
      navigatedRef.current = true;
      localStorage.setItem("token", data.token);
      const decoded = jwtDecode(data.token);
      const userData = {
        ...decoded,
        acesso_autorizacao: decoded.acesso_autorizacao ?? 0,
        tipo_programa:
          decoded.tipo === "usuario_ciee"
            ? null
            : (decoded.tipo_programa ?? null),
      };
      localStorage.setItem("user", JSON.stringify(userData));
      console.log("User logado:", userData);
      localStorage.setItem("empresa_id", decoded.empresa_id);

      if (decoded.tipo === "usuario_ciee") {
        localStorage.setItem("empresa_id", decoded.empresa_id || "");
        navigate("/dashboard");
        return { kind: "success" };
      }

      const tipoPrograma = Number(userData.tipo_programa);

      if (tipoPrograma === 0) {
        navigate("/tce");
        return { kind: "success" };
      }

      if (tipoPrograma === 1) {
        navigate("/tca");
        return { kind: "success" };
      }

      if (tipoPrograma === 2) {
        navigate("/tca-empregador");
        return { kind: "success" };
      }

      setErrors({
        user: "Tipo de programa inválido.",
      });
      return { kind: "error" };
    }

    setErrors({
      user: data && data.errors ? data.errors[0] : "Erro ao fazer login.",
    });
    return { kind: "error" };
  };

  const entrar = async (e) => {
    e.preventDefault();

    // Bloqueia re-submissão enquanto a request está em voo.
    if (loading) return;
    setErrors({});
    setLoading(true);
    // Reseta a flag de navegação a cada tentativa de login.
    navigatedRef.current = false;

    // Limpa defensivamente qualquer lixo do ESTUDANTE que tenha sobrado
    // da sessão anterior. Cenário típico: o usuário logou como estudante,
    // não clicou num logout que limpa TUDO (ex.: o Sair do Sidebar só
    // removia token/user/empresa_id), e agora vai logar como empresa.
    // Sem essa limpeza, campos como `estudante_id` aparecem pré-preenchidos
    // em formulários que checam essa chave.
    limparSessaoEstudante();

    try {
      let newErrors = {};

      const usuarioLimpo = usuario.replace(/\D/g, "");

      if (!usuarioLimpo) {
        newErrors.usuario = "Informe CPF ou Código da Empresa";
      }

      if (!senha) {
        newErrors.senha = "Senha em branco!";
      }

      if (usuarioLimpo) {
        if (usuarioLimpo.length === 11) {
          if (!validarCPF(usuarioLimpo)) {
            newErrors.usuario = "CPF inválido!";
          }
        } else if (usuarioLimpo.length > 6) {
          newErrors.usuario = "Código da empresa inválido";
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Fluxo CPF: tenta o login do estudante PRIMEIRO (em série).
      // O /tce/login-estudante foi feito pra ser rápido e retornar
      // 401/403/404 rapidamente pra qualquer usuário que NÃO é
      // estudante. Quando é estudante, já retorna token + tce_id
      // numa única chamada.
      //
      // Por que NÃO em paralelo (Promise.all) como antes?
      // Em paralelo, o /tokens pode terminar antes do /tce/login-estudante
      // e, se o CPF também existir como usuário de empresa, o token do
      // /tokens é salvo ANTES do token do estudante — o que resulta em
      // GET /tce/<id> retornando 403 (token errado) ou tela "Nenhuma
      // autorização vinculada" (rota errada). Essa race condition causava
      // comportamento intermitente: às vezes o login-estudante vencia, às
      // vezes o /tokens vencia. Em série, sempre dá prioridade ao login
      // de estudante (que é o caminho correto quando o usuário é
      // estudante) e cai pro /tokens apenas quando explicitamente
      // rejeitado (não é estudante).
      if (usuarioLimpo.length === 11 && validarCPF(usuarioLimpo)) {
        const estudanteResult = await tentarLoginEstudante(usuarioLimpo, {
          cpf: usuarioLimpo,
          senha,
        });

        // Sucesso do /tce/login-estudante → já navegou pro TCE.
        if (estudanteResult?.kind === "success") return;
        // Erro de autenticação real do estudante (400/500 com msg do
        // backend) → mostrar erro e parar; não tenta /tokens.
        if (estudanteResult?.kind === "error") return;
        // 'rejected' (401/403/404) → não é estudante; cai pro /tokens.
        // 'network' → também tenta /tokens como fallback (pode ser que
        // o endpoint do estudante esteja fora e o /tokens ainda responda).
        await tentarLoginEmpresa(usuarioLimpo, senha);
        return;
      }

      // Código de empresa (≤6 dígitos): fluxo direto, sem overhead do
      // endpoint de estudante.
      await tentarLoginEmpresa(usuarioLimpo, senha);
    } catch (err) {
      console.error("Erro na requisição:", err);
      setErrors({ user: "Erro ao conectar com o servidor." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" id="main-container">
      <form className="form-inicial login-form" onSubmit={entrar}>
        <div id="social_media">
          <img src="/logociee.png" alt="Logo" />
        </div>

        <div className="form_header">
          <h1>Login</h1>
        </div>

        <div id="inputs">
          <div className="input-box">
            <label htmlFor="usuario">CPF ou Código da Empresa</label>
            <div className="input-field">
              <i className="fa-solid fa-user"></i>
              <input
                type="text"
                id="usuario"
                name="usuario"
                className="input-tca"
                placeholder="000.000.000-00"
                maxLength="14"
                autoComplete="username"
                value={usuario}
                onChange={handleUsuarioChange}
                disabled={loading}
              />
            </div>
            <span className="error-message">{errors.usuario}</span>
          </div>

          <div className="input-box">
            <label htmlFor="senha">Senha</label>
            <div className="input-field">
              <i className="fa-solid fa-key"></i>
              <input
                type="password"
                id="senha"
                name="senha"
                placeholder="Senha"
                className="input-tca"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                disabled={loading}
              />
            </div>
            <span className="error-message">{errors.senha}</span>
          </div>
        </div>

        <div id="user-error">{errors.user}</div>

        <button
          className={`login${loading ? " login--loading" : ""}`}
          type="submit"
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className="login__spinner" aria-hidden="true" />
              <span>Acessando...</span>
            </>
          ) : (
            "Acessar"
          )}
        </button>
      </form>
    </div>
  );
};

export default Inicial;
